import React from 'react';
import Button from '@mui/material/Button';
import { ErrorCode, FileRejection } from 'react-dropzone';
import UploadFile from '@/components/form/UploadFile';
import { TRACK_NAME } from '@/constants/site-track';
import setAlertValue from '@/components/Alert';
import {
  sendEvent,
  trackClickEvent,
  useIntersectionObserver,
} from '@/utils/site-track';
import SvgUpload from './SvgUpload';

const RESUME_TYPE_AND_SIZE = {
  ACCEPT: {
    'image/jpg': [],
    'image/png': [],
    'image/jpeg': [],
    'application/msword': [],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      [],
    'application/pdf': [],
  },
  SIZE: 8388608,
  NAME: '8M',
};

const UploadResume = (props: {
  setResumeFile: React.Dispatch<React.SetStateAction<File | null>>;
  dataEleGroup?: string;
  setEvaluationStep?: React.Dispatch<React.SetStateAction<number>>;
  className?: string;
  submitButton?: React.ReactNode;
  icon?: React.ReactNode;
  smallSize?: boolean;
}) => {
  const {
    setResumeFile,
    dataEleGroup,
    setEvaluationStep,
    className = 'tablet:px-10 tablet:py-[100px]',
    submitButton,
    icon = <SvgUpload />,
    smallSize,
  } = props;
  const [observerEls, setObserverEls] = React.useState<Element[]>([]);
  const uploadRef = React.useRef(null);

  const handleDrop = React.useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[], event: any) => {
      dataEleGroup &&
        sendEvent({
          name: TRACK_NAME.upload,
          ele_id: `${event.type === 'change' ? 'click' : 'drag'}_上传简历`,
          ele_group: dataEleGroup,
        });
      const file = acceptedFiles[0];
      if (file) {
        setResumeFile(file);
        setEvaluationStep?.(1);
      }
    },
    [],
  );

  const handleDropRejected = React.useCallback(
    (fileRejections: FileRejection[]) => {
      const { code, message } = fileRejections[0].errors[0];
      if (code === ErrorCode.FileInvalidType) {
        setAlertValue({
          msg: `请上传DOC、DOCX、PDF、PNG、JPG、JPEG格式的文件`,
          severity: 'warning',
        });
      } else if (code === ErrorCode.FileTooLarge) {
        setAlertValue({
          msg: `文件大小不能超过${RESUME_TYPE_AND_SIZE.NAME}`,
          severity: 'warning',
        });
      } else {
        setAlertValue({ msg: message, severity: 'warning' });
      }
    },
    [],
  );

  useIntersectionObserver(observerEls);

  React.useEffect(() => {
    if (uploadRef.current && !observerEls.length && dataEleGroup) {
      setObserverEls([uploadRef.current]);
    }
  }, [uploadRef.current]);

  return (
    <div
      className="mx-auto tablet:max-w-[600px]"
      data-ele-group={dataEleGroup}
      data-ele-id="upload_resume"
      ref={uploadRef}
    >
      <UploadFile
        className={`flex flex-col items-center justify-center border border-dashed border-slate-300 rounded-lg px-4 py-4 ${className} tablet:bg-white`}
        noClick={true}
        noDrag={false}
        accept={RESUME_TYPE_AND_SIZE.ACCEPT}
        maxSize={RESUME_TYPE_AND_SIZE.SIZE}
        onDrop={handleDrop}
        onDropRejected={handleDropRejected}
      >
        <>
          {icon}
          <p
            className={`mt-8 ${smallSize ? 'text-sm' : 'text-xl font-semibold'}`}
          >
            将文件拖拽至框内
          </p>
          <p
            className={`mt-4 mb-10 text-center ${smallSize ? 'text-xs text-black/65' : 'text-base text-web-grey-600'}`}
          >
            {`支持 DOC、DOCX、PDF、PNG、JPG、JPEG格式，不超过${RESUME_TYPE_AND_SIZE.NAME}`}
          </p>
          {submitButton ? (
            submitButton
          ) : (
            <UploadFile
              noDragEventsBubbling={true}
              accept={RESUME_TYPE_AND_SIZE.ACCEPT}
              maxSize={RESUME_TYPE_AND_SIZE.SIZE}
              onDrop={handleDrop}
              onDropRejected={handleDropRejected}
            >
              <Button
                variant={smallSize ? 'outlined' : 'contained'}
                className={`${smallSize ? '!text-sm !font-normal !px-6 !py-3' : '!text-base !px-4.5 !py-2'}`}
                data-ele-group={dataEleGroup}
                data-ele-id="btn_选择文件"
                onClick={(e) => trackClickEvent(e)}
              >
                选择文件
              </Button>
            </UploadFile>
          )}
        </>
      </UploadFile>
    </div>
  );
};

export default UploadResume;
